import React from 'react';
import { connect } from 'react-redux';
import TooltipElement from '../components/tooltipElement';
import TooltipButtonGroup from '../components/tooltipButtonGroup';
import ClientTable from '../components/clientTable';
import OverlayLoader from '../components/overlayLoader';
import TableSelectionManager from '../components/tableSelectionManager';
import * as clientsActions from '../store/clientsSlice';
import { clientsSelectors } from '../store/clientsSlice';
import ClientEditDialog from './clientEditDialog';
import KeyLicenseEditDialog from './keyLicenseEditDialog';
import AssignLicenseToClientDialog from './assignLicenseToClientDialog';
import OfflineLicenseEditDialog from './offlineLicenseEditDialog';
import ClientHistoryDialog from './clientHistoryDialog';
import WebViewerServerAddDialog from './webViewerServerAddDialog';
import AssignWebViewerServerToClientDialog from './assignWebViewerServerToClientDialog';
import setTitleToFilters from '../common/filtersTitles';
import ModalMessage from './modalMessage';

import './common.css';
import { KeyLicenseTypes } from '../common/keyLicenseTypes';
import { showRowIdByCtrlClick } from '../common/tableActions';
import { permanentLicensesSelectors } from '../store/permanentLicensesSlice';

const thatObjectName = 'clients';

class ClientsContainer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showClientEditDialog: false,
      clientEditDialogMode: ClientEditDialog.Mode.Create,
      showPermLicEditDialog: false,
      showAssignLicenseDialog: false,
      showOfflineEditDialog: false,
      showHistoryDialog: false,
      temporalType: undefined,
      showWebViewerServerAddDialog: false,
      showAssignWebViewerServerDialog: false,
    };
  }

  componentDidMount() {
    setTitleToFilters();
    this.props.fetchAllClients();
  }

  render() {
    return (
      <OverlayLoader active={this.isLoading()} text={this.getLoaderMessage()}>
        <div>
          <h1>Clients</h1>
          {this.renderButtons()}
          {this.renderContent()}
          {this.renderClientEditDialog()}
          {this.renderModalMessage()}
          {this.renderPermLicCreateDialog()}
          {this.renderAssignLicenseDialog()}
          {this.renderOfflineLicenseEditDialog()}
          {this.renderHistoryDialog()}
          {this.renderWebViewerServerAddDialog()}
          {this.renderAssignWebViewerServerDialog()}
        </div>
      </OverlayLoader>
    );
  }

  renderModalMessage = () => <ModalMessage />;

  renderButtons = () => (
    <TooltipButtonGroup>
      <TooltipElement type="button" text="Refresh" icon="refresh" onClick={this.refreshClients} />
      <TooltipElement type="button" text="Create client" icon="plus" onClick={this.createNewClient} displayOnlyForWriter />
      <TooltipElement type="button" text="Edit client" icon="edit" onClick={this.editClient}
                      disabled={this.isEditButtonDisabled()} displayOnlyForWriter />
      <TooltipElement type="button" text="Show client history" icon="list-alt" onClick={this.showClientHistory}
                      disabled={this.isHistoryButtonDisabled()} />
      <TooltipElement type="dropdown" text="Create ..." disabled={this.isCreateLicenseSelectDisabled()}
                      icon="plus-sign" id="create-licenses-select" displayOnlyForWriter
                      items={[
                        {
                          onSelect: () => this.createNewOnlineLicenses(KeyLicenseTypes.OnlinePermanent),
                          disabled: this.isAddOnlineLicenseButtonDisabled(),
                          text: 'Online permanent license',
                        },
                        {
                          onSelect: () => this.addOfflineLicense(KeyLicenseTypes.Offline),
                          disabled: this.isAddOfflineLicenseButtonDisabled(),
                          text: 'Offline permanent license',
                        },
                        { divider: true },
                        {
                          onSelect: () => this.createNewOnlineLicenses(KeyLicenseTypes.OnlineTemporary),
                          disabled: this.isAddOnlineLicenseButtonDisabled(),
                          text: 'Online temporary license',
                        },
                        { divider: true },
                        {
                          onSelect: () => this.AddWebViewerServer(),
                          disabled: this.isAddWebViewerServerButtonDisabled(),
                          text: 'Web viewer server',
                        },
                      ]}>
      </TooltipElement>
      <TooltipElement type="dropdown" text="Assign to client" disabled={this.isAssignSelectDisabled()}
                      icon="link" displayOnlyForWriter
                      items={[
                        {
                          onSelect: this.assignPermanentLicense,
                          disabled: this.isAssignLicenseButtonDisabled(),
                          text: 'Permanent license',
                        },
                        { divider: true },
                        {
                          onSelect: this.assignWebViewerServer,
                          disabled: this.isAssignWebViewerServerButtonDisabled(),
                          text: 'Web viewer server',
                        },
                      ]}>
      </TooltipElement>
    </TooltipButtonGroup>
  );

  showClientHistory = () => {
    this.setState({ showHistoryDialog: true });
  };

  isCreateLicenseSelectDisabled = () =>
    this.isAddOnlineLicenseButtonDisabled() && this.isAddOfflineLicenseButtonDisabled() && this.isAddWebViewerServerButtonDisabled();

  isAssignSelectDisabled = () => 
    this.isAssignLicenseButtonDisabled() && this.isAssignWebViewerServerButtonDisabled();

  isHistoryButtonDisabled = () => this.props.selectedClients.length !== 1;

  isEditButtonDisabled = () => this.props.selectedClients.length !== 1;

  isAddOnlineLicenseButtonDisabled = () => this.props.selectedClients.length !== 1;

  isAssignLicenseButtonDisabled = () => this.props.selectedClients.length !== 1;

  isAddOfflineLicenseButtonDisabled = () => this.props.selectedClients.length !== 1;

  isAddWebViewerServerButtonDisabled = () => this.props.selectedClients.length !== 1;

  isAssignWebViewerServerButtonDisabled = () => this.props.selectedClients.length !== 1;

  closeHistoryDialog = () => {
    this.setState({ showHistoryDialog: false });
  };

  getActionsForFavorites = () => ({
    addToFavorites: this.props.addClientToFavorites,
    removeFromFavorites: this.props.removeClientFromFavorites,
  });

  renderContent = () => {
    if (this.props.clientsFetchingError)
      return this.renderError();

    const tableRowEvents = {
      onClick: this.handleRowClick,
    };

    return (
      <TableSelectionManager
        items={this.props.clients}
        objectName={thatObjectName}
      >
        <ClientTable
          clients={this.props.clients}
          actions={this.getActionsForFavorites()}
          rowEvents={tableRowEvents}
        />
      </TableSelectionManager>
    );
  };

  renderError = () => (
    <p className="text-danger">Error: {this.props.clientsFetchingError.toString()}</p>
  );

  isLoading = () => this.props.isClientsFetching
    || this.props.isLicenseAssignmentInProcess
    || this.props.isClientUpdating;

  getLoaderMessage = () => {
    if (this.props.isClientsFetching) {
      return "Clients loading...";
    }
    if (this.props.isLicenseAssignmentInProcess) {
      return "License assignment...";
    }
    if (this.props.isClientUpdating) {
      return "Clients loading...";
    }
  };

  renderHistoryDialog = () => {
    return (
      <ClientHistoryDialog
        show={this.state.showHistoryDialog}
        onClose={this.closeHistoryDialog}
        id={this.getSelectedClientFieldValue('id')}
        fullName={this.getSelectedClientFieldValue('fullName')}
        formalName={this.getSelectedClientFieldValue('formalName')}
        legalAddress={this.getSelectedClientFieldValue('legalAddress')}
        legalForm={this.getSelectedClientFieldValue('legalForm')}
        name={this.getSelectedClientFieldValue('name')}
        taxId={this.getSelectedClientFieldValue('taxId')}
        phone={this.getSelectedClientFieldValue('phone')}
      />
    );
  };

  renderClientEditDialog = () => (
    <ClientEditDialog
      mode={this.state.clientEditDialogMode}
      onClose={this.closeClientEditDialog}
      show={this.state.showClientEditDialog}
      id={this.getSelectedClientFieldValue('id')}
      legalForm={this.getSelectedClientFieldValue('legalForm')}
      name={this.getSelectedClientFieldValue('name')}
      legalAddress={this.getSelectedClientFieldValue('legalAddress')}
      taxId={this.getSelectedClientFieldValue('taxId')}
      contactPerson={this.getSelectedClientFieldValue('contactPerson')}
      e_mail={this.getSelectedClientFieldValue('e_mail')}
      phone={this.getSelectedClientFieldValue('phone')}
    />
  );

  renderPermLicCreateDialog = () => (
    <KeyLicenseEditDialog
      mode={KeyLicenseEditDialog.Mode.Create}
      onClose={this.closePermLicEditDialog}
      show={this.state.showPermLicEditDialog}
      clientId={this.getSelectedClientFieldValue('id')}
      clientName={this.getSelectedClientFieldValue('fullName')}
      temporalType={this.state.temporalType}
    />
  );

  renderAssignLicenseDialog = () => (
    <AssignLicenseToClientDialog
      onClose={this.closeAssignLicenseDialog}
      show={this.state.showAssignLicenseDialog}
      clientId={this.getSelectedClientFieldValue('id')}
      clientName={this.getSelectedClientFieldValue('fullName')}
    />
  );

  renderOfflineLicenseEditDialog = () => (
    <OfflineLicenseEditDialog
      mode={OfflineLicenseEditDialog.Mode.Create}
      onClose={this.closeOfflineLicenseDialog}
      show={this.state.showOfflineEditDialog}
      clientId={this.getSelectedClientFieldValue('id')}
      clientName={this.getSelectedClientFieldValue('fullName')}
      temporalType={this.state.temporalType}
    />
  );

  renderWebViewerServerAddDialog = () => (
    <WebViewerServerAddDialog
      mode={0}
      onClose={this.closeWebViewerServerAddDialog}
      show={this.state.showWebViewerServerAddDialog}
      clientId={this.getSelectedClientFieldValue('id')}
      clientName={this.getSelectedClientFieldValue('fullName')}
    />
  );

  renderAssignWebViewerServerDialog = () => (
    <AssignWebViewerServerToClientDialog
      onClose={this.closeAssignWebViewerServerDialog}
      show={this.state.showAssignWebViewerServerDialog}
      clientId={this.getSelectedClientFieldValue('id')}
      clientName={this.getSelectedClientFieldValue('fullName')}
    />
  )

  getSelectedClientFieldValue = (fieldName, defaultValue = null) => {
    const client = this.props.selectedClients[0];
    if (!client) return null;
    try {
      const value = fieldName.split('.').reduce((acc, key) => acc[key], client);
      return value === undefined ? defaultValue : value === null ? defaultValue : value;
    } catch (e) {
      return defaultValue;
    }
  };


  refreshClients = () => {
    this.props.fetchAllClients();
  };

  createNewClient = () => {
    this.setState({
      clientEditDialogMode: ClientEditDialog.Mode.Create,
      showClientEditDialog: true,
    });
  };

  editClient = () => {
    this.setState({
      clientEditDialogMode: ClientEditDialog.Mode.Edit,
      showClientEditDialog: true
    });
  };

  closeClientEditDialog = () => {
    this.setState({ showClientEditDialog: false });
  };

  createNewOnlineLicenses = (temporalType) => {
    this.setState({ temporalType, showPermLicEditDialog: true });
  };

  closePermLicEditDialog = () => {
    this.setState({ showPermLicEditDialog: false });
  };

  assignPermanentLicense = () => {
    this.setState({ showAssignLicenseDialog: true });
  };

  closeAssignLicenseDialog = () => {
    this.setState({ showAssignLicenseDialog: false });
  };

  addOfflineLicense = (temporalType) => {
    this.setState({ temporalType, showOfflineEditDialog: true });
  };

  closeOfflineLicenseDialog = () => {
    this.setState({ showOfflineEditDialog: false });
  };

  AddWebViewerServer = () => {
    this.setState({ showWebViewerServerAddDialog: true });
  };

  closeWebViewerServerAddDialog = () => {
    this.setState({ showWebViewerServerAddDialog: false });
  };

  assignWebViewerServer = () => {
    this.setState({ showAssignWebViewerServerDialog: true });
  };

  closeAssignWebViewerServerDialog = () => {
    this.setState({ showAssignWebViewerServerDialog: false });
  };

  handleRowClick = (e, row) => {
    e.stopPropagation();
    showRowIdByCtrlClick(e, row, 'Client ID');
  }
}

const mapStateToProps = state => ({
  isClientsFetching: clientsSelectors.isFetching(state),
  isClientUpdating: clientsSelectors.isUpdating(state),
  clients: clientsSelectors.selectAll(state),
  clientsFetchingError: clientsSelectors.fetchingError(state),
  selectedClients: clientsSelectors.selectedClients(state),

  isLicenseAssignmentInProcess: permanentLicensesSelectors.isAssignmentInProcess(state),
});

const actionCreators = {
  fetchAllClients: clientsActions.fetchAll,
  addClientToFavorites: clientsActions.addToFavorites,
  removeClientFromFavorites: clientsActions.removeFromFavorites,
};

export default connect(mapStateToProps, actionCreators)(ClientsContainer);



// WEBPACK FOOTER //
// ./src/containers/clientsContainer.js